// The manifest kore's SearchBar renders for the locations table: which facets a
// user may filter on, with which operators, and which columns sort.
//
// Declared here rather than fetched from a /facets endpoint: our value sets
// are either static (service status) or free text (version), so a manifest
// endpoint would be hand-written duplication. Swapping this constant for a
// fetch later changes no UI.
//
// Every operator listed is one `locations_search.rs` actually translates — the
// picker offers exactly what is listed, so an operator listed here but
// unhandled server-side is a filter that silently does nothing. Notable
// omissions, all deliberate:
//   * `Eq`/`Neq` on last-seen — implemented, but exact equality against an
//     instant is never the question anyone is asking of a timestamp.
//   * `Gt`/`Lt` on last-seen — the inclusive bounds and `Between` cover it.
//   * any sort on version — sorting version strings puts 0.10.0 below 0.9.0,
//     which would mislead on exactly the rollout question the column looks
//     like it answers. The server declares no such sort field either.

import { type FilterDef, FilterOperator, type FilterResponse } from "@pej/kore/components";
import type {
    EnumOp,
    LocationFilters,
    LocationQueryField,
    LocationSortField,
    ServiceStatus,
    TemporalOp,
    TextOp,
} from "@/api/types";
import { acceptedOp } from "@/features/search/wire-op";

const LOCATION_FILTER_DEFS: FilterDef[] = [
    {
        type: "Enum",
        filterParam: "serviceStatus",
        label: "Posting service status",
        operators: [FilterOperator.Eq, FilterOperator.Neq],
        values: [
            { value: "Online", label: "Online" },
            { value: "Offline", label: "Offline" },
            { value: "Never", label: "Never connected" },
        ],
    },
    {
        type: "Text",
        filterParam: "serviceVersion",
        label: "Posting service version",
        operators: [FilterOperator.Contains, FilterOperator.NotContains],
    },
    {
        type: "Instant",
        filterParam: "lastSeen",
        label: "Last seen",
        operators: [FilterOperator.Gte, FilterOperator.Lte, FilterOperator.Between],
    },
];

/**
 * `sortBy.fields` carries the server's `LocationSortField` names verbatim, because
 * kore matches them against DataTable column keys — see the two PascalCase
 * keys in `locations-table.tsx`.
 *
 * `query.fields` is omitted on purpose, not `query` itself: locations have
 * exactly one searchable column (`LocationQueryField` is just `"Name"`), so
 * there is no scope to pick between — declaring `fields` would only add a
 * scope chip with one option. `query` still has to be present (even as just
 * a placeholder) for `SearchBar` to render its free-text input at all; a
 * `null` manifest `query` means "this endpoint supports no text search",
 * which isn't true here.
 */
export const LOCATIONS_SEARCH_MANIFEST: FilterResponse = {
    filters: LOCATION_FILTER_DEFS,
    query: {
        placeholder: "Search locations…",
    },
    sortBy: {
        fields: [
            { field: "Name", label: "Name" },
            { field: "LastSeen", label: "Last seen" },
        ],
        default: { field: "Name", order: "Asc" },
    },
};

/**
 * Every option the footer offers has to sit inside the server's 1..=1000
 * (Kotlin's search bounds); the page always sends one, so the server's own
 * default of 20 never applies.
 */
export const LOCATIONS_PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * `LocationSortField` and `LocationQueryField` restated as values, for
 * `usePageSearch`'s guards. Typed as the wire enums rather than
 * `readonly string[]`, so dropping a variant from `api/types.ts` fails to
 * compile here instead of shipping a field the endpoint would reject.
 *
 * `LOCATION_QUERY_FIELDS` lists the one column `locations_search.rs` searches even
 * though `LOCATIONS_SEARCH_MANIFEST` declares no `query.fields` (see its doc): no
 * scope chip means kore can only ever hand back `null`, so the list is what
 * the wire accepts rather than what the UI can produce.
 */
export const LOCATION_SORT_FIELDS: readonly LocationSortField[] = ["Name", "LastSeen"];
export const LOCATION_QUERY_FIELDS: readonly LocationQueryField[] = ["Name"];

/** The server's own default, restated so the first request is explicit. */
export const LOCATION_DEFAULT_ORDER: { field: LocationSortField; order: "Asc" | "Desc" } = {
    field: "Name",
    order: "Asc",
};

/**
 * The shape `useKoreSearch<LocationSearchBarFilters>` actually produces from
 * `LOCATION_FILTER_DEFS` — distinct from `LocationFilters` (the wire shape
 * `locations_search.rs` expects), because kore's URL grammar is wider than either
 * the defs above or the server's own op enums:
 *
 *   * `lastSeen` is whatever op the URL names, checked against the
 *     `TemporalOp` variants the server deserializes.
 *   * `serviceStatus` carries raw URL strings, not `ServiceStatus` values. The
 *     server's `ServiceStatus` is a closed enum, so `?serviceStatus=Bogus` is
 *     a 400.
 *
 * Neither bad value is reachable through the picker; both are reachable from
 * a hand-edited or bookmarked URL, and both are sticky — every request fails
 * until the URL is edited back. Kept a separate type rather than reusing
 * `LocationFilters` with an `as` cast so the compiler catches a call site that
 * forgets `locationFiltersForWire`, exactly as `ScreenSearchBarFilters` does for
 * screens.
 */
export interface LocationSearchBarFilters {
    serviceStatus?: EnumOp<string>;
    serviceVersion?: TextOp;
    lastSeen?: { type: string };
}

/** Every `TemporalOp` variant `locations_search.rs` can deserialize. */
const TEMPORAL_WIRE_OPS: readonly TemporalOp["type"][] = ["Eq", "Neq", "Lt", "Lte", "Gt", "Gte", "Between"];

const SERVICE_STATUSES: readonly ServiceStatus[] = ["Online", "Offline", "Never"];

/**
 * Translate kore's raw filter object into the wire shape `locations_search.rs`
 * accepts, dropping what it cannot express. Mirrors the screens page's
 * translation step (`screenFiltersForWire`).
 */
export function locationFiltersForWire(raw: LocationSearchBarFilters | undefined): LocationFilters {
    return {
        serviceStatus: serviceStatusFilter(raw?.serviceStatus),
        serviceVersion: raw?.serviceVersion,
        lastSeen: acceptedOp<TemporalOp>(raw?.lastSeen, TEMPORAL_WIRE_OPS),
    };
}

/**
 * Drop values outside the server's `ServiceStatus` enum rather than the whole
 * chip: a URL naming two real statuses and one typo still asks a question the
 * endpoint can answer, and answering it beats a 400. An all-typo chip leaves
 * nothing to filter on and is dropped entirely.
 */
function serviceStatusFilter(op: EnumOp<string> | undefined): EnumOp<ServiceStatus> | undefined {
    if (op === undefined) return undefined;
    const values = op.values.filter((value): value is ServiceStatus =>
        (SERVICE_STATUSES as readonly string[]).includes(value),
    );
    if (values.length === 0) return undefined;
    return { type: op.type, values };
}
